import {
  CalendarDays,
  Clock3,
  MapPin,
  Minus,
  Plus,
  ShieldCheck,
  UserCircle2,
  Volume2,
  VolumeX,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { publicApi } from "../../api/client";
import LoadingState from "../../components/common/LoadingState";
import Footer from "../../components/feature/Footer";
import Navbar from "../../components/feature/Navbar";
import { useCustomerAuth } from "../../context/CustomerAuthContext";
import type { EventItem, RegistrationFormResponses } from "../../types/api";
import { getEventAuthActionLabel } from "../../utils/eventLabels";
import { formatCurrency, formatEventDate, formatRelativeDate } from "../../utils/format";
import {
  emptyMasterclassRegistration,
  isStorytellingMasterclassEvent,
  loadMasterclassRegistration,
  saveMasterclassRegistration,
} from "../../utils/masterclassRegistration";

function formatCompact(date: string) {
  return new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  }).format(new Date(date));
}

export default function EventDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { customer } = useCustomerAuth();
  const [event, setEvent] = useState<EventItem | null>(null);
  const [selectedTierId, setSelectedTierId] = useState<string>("");
  const [quantity, setQuantity] = useState(1);
  const [masterclassRegistration, setMasterclassRegistration] =
    useState<RegistrationFormResponses>(emptyMasterclassRegistration);
  const [registrationError, setRegistrationError] = useState<string | null>(null);
  const [isHeroMuted, setIsHeroMuted] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setIsLoading(true);
    void publicApi
      .getEvent(id)
      .then(({ event: data }) => {
        setEvent(data);
        setSelectedTierId(data.ticketTiers[0]?.id ?? "");
      })
      .catch((requestError) =>
        setError(requestError instanceof Error ? requestError.message : "Could not load event")
      )
      .finally(() => setIsLoading(false));
  }, [id]);

  const selectedTier = useMemo(
    () => event?.ticketTiers.find((tier) => tier.id === selectedTierId) ?? event?.ticketTiers[0] ?? null,
    [event, selectedTierId]
  );

  useEffect(() => {
    if (!event || !isStorytellingMasterclassEvent(event)) return;

    const saved = loadMasterclassRegistration(event.id)?.responses;
    setMasterclassRegistration((current) => ({
      ...emptyMasterclassRegistration,
      ...current,
      ...saved,
      fullName: saved?.fullName || current.fullName || customer?.name || "",
      email: saved?.email || current.email || customer?.email || "",
      phone: saved?.phone || current.phone || customer?.phone || "",
    }));
  }, [customer?.email, customer?.name, customer?.phone, event]);

  if (isLoading) {
    return (
      <div className="aan-ticket-flow min-h-screen bg-black pt-36 text-white">
        <div className="section-frame">
          <LoadingState label="Loading event..." />
        </div>
      </div>
    );
  }

  if (!event || error) {
    return (
    <div className="aan-ticket-flow min-h-screen bg-black text-white">
      <Navbar />
      <div className="section-frame pt-36">
        <div className="surface-card px-6 py-12 text-center text-sm font-semibold text-red-500">
            {error ?? "Event not found"}
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const subtotal = (selectedTier?.price ?? 0) * quantity;
  const remaining = selectedTier ? selectedTier.quantity - selectedTier.sold : 0;
  const isMasterclass = isStorytellingMasterclassEvent(event);

  const updateMasterclassRegistration = (field: keyof RegistrationFormResponses, value: string) => {
    setRegistrationError(null);
    setMasterclassRegistration((current) => ({
      ...current,
      [field]: value,
      ...(field === "attendedMasterclass" && value !== "Yes" ? { attendedMasterclassList: "" } : {}),
    }));
  };

  const continueToCheckout = () => {
    if (!event || !selectedTier) return;

    if (isMasterclass) {
      const responses = Object.fromEntries(
        Object.entries(masterclassRegistration).map(([key, value]) => [key, value.trim()])
      ) as unknown as RegistrationFormResponses;

      const requiredFields: Array<[keyof RegistrationFormResponses, string]> = [
        ["fullName", "Full name"],
        ["gender", "Gender"],
        ["phone", "Phone number"],
        ["email", "Email address"],
        ["occupation", "Occupation"],
        ["institution", "Institution"],
        ["attendedMasterclass", "Have you ever attended a masterclass"],
        ["goals", "What you hope to achieve"],
      ];
      const missingField = requiredFields.find(([field]) => !responses[field]);

      if (missingField) {
        setRegistrationError(`${missingField[1]} is required.`);
        return;
      }

      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(responses.email)) {
        setRegistrationError("Enter a valid email address.");
        return;
      }

      if (responses.attendedMasterclass === "Yes" && !responses.attendedMasterclassList) {
        setRegistrationError("List the masterclass(es) you have attended.");
        return;
      }

      saveMasterclassRegistration(event.id, responses);
    }

    navigate(`/checkout/${event.id}?tier=${selectedTier.id}&qty=${quantity}`);
  };

  return (
    <div className="aan-ticket-flow min-h-screen bg-black text-white">
      <Navbar />

      <section className="pt-32">
        <div className="section-frame">
          <div className="surface-card overflow-hidden">
            <div className="grid gap-0 lg:grid-cols-[1.2fr_0.8fr]">
              <div className="relative min-h-[380px] overflow-hidden">
                {event.trailerVideo ? (
                  <video
                    src={event.trailerVideo}
                    autoPlay
                    muted={isHeroMuted}
                    loop
                    playsInline
                    preload="auto"
                    poster={event.image}
                    className="h-full w-full object-cover"
                    aria-label={`${event.title} trailer`}
                  />
                ) : (
                  <img src={event.image} alt={event.title} className="h-full w-full object-cover" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/15 to-transparent" />
                {event.trailerVideo && (
                  <button
                    type="button"
                    onClick={() => setIsHeroMuted((current) => !current)}
                    className="absolute right-6 top-6 z-10 flex h-12 w-12 items-center justify-center rounded-full bg-black/65 text-white shadow-xl backdrop-blur transition hover:bg-black focus:outline-none focus:ring-2 focus:ring-aan-green"
                    aria-label={isHeroMuted ? `Unmute ${event.title} trailer` : `Mute ${event.title} trailer`}
                    title={isHeroMuted ? "Unmute trailer" : "Mute trailer"}
                  >
                    {isHeroMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
                  </button>
                )}
                <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8">
                  <div className="mb-3 inline-flex rounded-full bg-aan-green px-4 py-2 text-xs font-semibold uppercase tracking-[0.24em] text-black">
                    {event.category}
                  </div>
                  <h1 className="max-w-3xl font-display text-4xl font-black uppercase leading-none text-white sm:text-5xl">{event.title}</h1>
                  <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-white/80">
                    <span>{formatRelativeDate(event.date)}</span>
                    <span>-</span>
                    <span>{event.city}</span>
                    <span>-</span>
                    <span>{event.status.replace("-", " ")}</span>
                  </div>
                </div>
              </div>

              <div className="space-y-5 p-6 sm:p-8">
                <div className="text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">Choose ticket type</div>

                <div className="space-y-3">
                  {event.ticketTiers.map((tier) => {
                    const available = tier.quantity - tier.sold;
                    return (
                      <button
                        key={tier.id}
                        type="button"
                        onClick={() => {
                          setSelectedTierId(tier.id);
                          setQuantity(1);
                        }}
                        className={`w-full rounded-[24px] border p-5 text-left transition ${
                          selectedTier?.id === tier.id
                            ? "border-aan-green bg-black text-white"
                            : "border-line bg-surface hover:border-[var(--pp-gold)]/60"
                        }`}
                      >
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <div className="text-xl font-bold">{tier.name}</div>
                            <div className={`${selectedTier?.id === tier.id ? "text-slate-300" : "text-muted-foreground"} mt-1 text-sm`}>
                              {tier.description}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-2xl font-bold">{formatCurrency(tier.price)}</div>
                            <div className="text-xs uppercase tracking-[0.2em] text-slate-400">{available} left</div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>

                <div className="rounded-[8px] bg-surface-muted p-5">
                  <div className="mb-3 text-sm font-semibold text-muted-foreground">Quantity</div>
                  <div className="flex items-center justify-between rounded-full border border-line bg-surface px-4 py-3">
                    <button
                      type="button"
                      onClick={() => setQuantity((value) => Math.max(1, value - 1))}
                      className="flex h-10 w-10 items-center justify-center rounded-full bg-surface-muted"
                    >
                      <Minus size={16} />
                    </button>
                    <div className="text-xl font-bold">{quantity}</div>
                    <button
                      type="button"
                      onClick={() => setQuantity((value) => Math.min(remaining || 1, value + 1))}
                      className="flex h-10 w-10 items-center justify-center rounded-full bg-surface-muted"
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                </div>

                {isMasterclass && (
                  <div className="rounded-[8px] border border-line bg-surface-muted p-5">
                    <div className="font-display text-xs font-semibold uppercase tracking-[0.2em] text-aan-bright">
                      Registration
                    </div>
                    <div className="mt-4 grid gap-4">
                      <label>
                        <span className="mb-2 block text-sm font-semibold text-muted-foreground">Full Name *</span>
                        <input
                          type="text"
                          required
                          value={masterclassRegistration.fullName}
                          onChange={(inputEvent) =>
                            updateMasterclassRegistration("fullName", inputEvent.target.value)
                          }
                          className="w-full rounded-[20px] border border-line bg-surface px-4 py-3 text-sm text-foreground outline-none focus:border-aan-green"
                        />
                      </label>

                      <div className="grid gap-4 sm:grid-cols-2">
                        <label>
                          <span className="mb-2 block text-sm font-semibold text-muted-foreground">Gender *</span>
                          <select
                            required
                            value={masterclassRegistration.gender}
                            onChange={(inputEvent) =>
                              updateMasterclassRegistration("gender", inputEvent.target.value)
                            }
                            className="w-full rounded-[20px] border border-line bg-surface px-4 py-3 text-sm text-foreground outline-none focus:border-aan-green"
                          >
                            <option value="">Select</option>
                            <option value="Female">Female</option>
                            <option value="Male">Male</option>
                            <option value="Prefer not to say">Prefer not to say</option>
                          </select>
                        </label>
                        <label>
                          <span className="mb-2 block text-sm font-semibold text-muted-foreground">Phone number *</span>
                          <input
                            type="tel"
                            required
                            value={masterclassRegistration.phone}
                            onChange={(inputEvent) =>
                              updateMasterclassRegistration("phone", inputEvent.target.value)
                            }
                            className="w-full rounded-[20px] border border-line bg-surface px-4 py-3 text-sm text-foreground outline-none focus:border-aan-green"
                          />
                        </label>
                      </div>

                      <label>
                        <span className="mb-2 block text-sm font-semibold text-muted-foreground">Email address *</span>
                        <input
                          type="email"
                          required
                          value={masterclassRegistration.email}
                          onChange={(inputEvent) =>
                            updateMasterclassRegistration("email", inputEvent.target.value)
                          }
                          className="w-full rounded-[20px] border border-line bg-surface px-4 py-3 text-sm text-foreground outline-none focus:border-aan-green"
                        />
                      </label>

                      <div className="grid gap-4 sm:grid-cols-2">
                        <label>
                          <span className="mb-2 block text-sm font-semibold text-muted-foreground">Occupation *</span>
                          <input
                            type="text"
                            required
                            value={masterclassRegistration.occupation}
                            onChange={(inputEvent) =>
                              updateMasterclassRegistration("occupation", inputEvent.target.value)
                            }
                            className="w-full rounded-[20px] border border-line bg-surface px-4 py-3 text-sm text-foreground outline-none focus:border-aan-green"
                          />
                        </label>
                        <label>
                          <span className="mb-2 block text-sm font-semibold text-muted-foreground">Institution *</span>
                          <input
                            type="text"
                            required
                            value={masterclassRegistration.institution}
                            onChange={(inputEvent) =>
                              updateMasterclassRegistration("institution", inputEvent.target.value)
                            }
                            className="w-full rounded-[20px] border border-line bg-surface px-4 py-3 text-sm text-foreground outline-none focus:border-aan-green"
                          />
                        </label>
                      </div>

                      <label>
                        <span className="mb-2 block text-sm font-semibold text-muted-foreground">
                          Have you ever attended a masterclass? *
                        </span>
                        <select
                          required
                          value={masterclassRegistration.attendedMasterclass}
                          onChange={(inputEvent) =>
                            updateMasterclassRegistration("attendedMasterclass", inputEvent.target.value)
                          }
                          className="w-full rounded-[20px] border border-line bg-surface px-4 py-3 text-sm text-foreground outline-none focus:border-aan-green"
                        >
                          <option value="">Select</option>
                          <option value="Yes">Yes</option>
                          <option value="No">No</option>
                        </select>
                      </label>

                      {masterclassRegistration.attendedMasterclass === "Yes" && (
                        <label>
                          <span className="mb-2 block text-sm font-semibold text-muted-foreground">
                            If yes, kindly list which masterclass(es) you attended *
                          </span>
                          <textarea
                            required
                            rows={3}
                            value={masterclassRegistration.attendedMasterclassList}
                            onChange={(inputEvent) =>
                              updateMasterclassRegistration("attendedMasterclassList", inputEvent.target.value)
                            }
                            className="w-full rounded-[20px] border border-line bg-surface px-4 py-3 text-sm text-foreground outline-none focus:border-aan-green"
                          />
                        </label>
                      )}

                      <label>
                        <span className="mb-2 block text-sm font-semibold text-muted-foreground">
                          What do you hope to achieve from the storytelling masterclass with Uncle Ebo Whyte? *
                        </span>
                        <textarea
                          required
                          rows={4}
                          value={masterclassRegistration.goals}
                          onChange={(inputEvent) =>
                            updateMasterclassRegistration("goals", inputEvent.target.value)
                          }
                          className="w-full rounded-[20px] border border-line bg-surface px-4 py-3 text-sm text-foreground outline-none focus:border-aan-green"
                        />
                      </label>
                    </div>

                    {registrationError && (
                      <div className="mt-4 rounded-[20px] border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-600">
                        {registrationError}
                      </div>
                    )}
                  </div>
                )}

                <div className="rounded-[8px] border border-white/15 bg-black p-5 text-white">
                  <div className="flex items-center justify-between text-sm text-slate-300">
                    <span>{selectedTier?.name}</span>
                    <span>{quantity} x</span>
                  </div>
                  <div className="mt-4 text-4xl font-bold">{formatCurrency(subtotal)}</div>
                  <div className="mt-2 flex items-center gap-2 text-sm text-emerald-300">
                    <ShieldCheck size={16} />
                    Ticket is generated instantly after payment confirmation
                  </div>
                  <button
                    type="button"
                    onClick={continueToCheckout}
                    className="mt-5 w-full rounded-full bg-aan-green px-5 py-4 font-display text-base font-black uppercase text-black hover:bg-aan-bright"
                  >
                    {getEventAuthActionLabel(event, Boolean(customer))}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-24 pt-10">
        <div className="section-frame">
          <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
            <div className="space-y-6">
              <div className="surface-card p-6 sm:p-8">
                <h2 className="font-display text-3xl font-black uppercase text-foreground">About the event</h2>
                <p className="mt-4 text-sm leading-8 text-muted-foreground">{event.description}</p>
                <div className="mt-6 grid gap-3 sm:grid-cols-2">
                  {event.highlights.map((highlight) => (
                    <div key={highlight} className="rounded-[20px] bg-surface-muted px-4 py-4 text-sm font-medium text-muted-foreground">
                      {highlight}
                    </div>
                  ))}
                </div>
              </div>

              {event.trailerVideo && (
                <div className="surface-card p-6 sm:p-8">
                  <h2 className="text-3xl font-bold text-foreground">Trailer</h2>
                  <video
                    src={event.trailerVideo}
                    controls
                    preload="metadata"
                    poster={event.image}
                    className="mt-5 aspect-video w-full rounded-[24px] bg-black object-contain"
                  />
                </div>
              )}

              {!!event.gallery.length && (
                <div className="surface-card p-6 sm:p-8">
                  <h2 className="font-display text-3xl font-black uppercase text-foreground">Gallery</h2>
                  <div className="mt-5 grid gap-4 md:grid-cols-2">
                    {event.gallery.map((image) => (
                      <img key={image} src={image} alt={event.title} className="h-60 w-full rounded-[24px] object-cover" />
                    ))}
                  </div>
                </div>
              )}
            </div>

            <aside className="space-y-6">
              <div className="surface-card p-6">
                <div className="mb-5 text-xs font-semibold uppercase tracking-[0.24em] text-aan-bright">Event details</div>
                <div className="space-y-4 text-sm text-muted-foreground">
                  <div className="flex gap-3">
                    <CalendarDays size={18} className="mt-1 text-[var(--pp-salmon)]" />
                    <div>
                      <div className="font-semibold text-foreground">{formatEventDate(event.date, event.time)}</div>
                      <div className="text-muted-foreground">{formatCompact(event.date)}</div>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Clock3 size={18} className="mt-1 text-[var(--pp-teal)]" />
                    <div>
                      <div className="font-semibold text-foreground">{event.time}</div>
                      <div className="text-muted-foreground">Doors open {event.doorsOpen || "1 hour before showtime"}</div>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <MapPin size={18} className="mt-1 text-[var(--pp-gold)]" />
                    <div>
                      <div className="font-semibold text-foreground">{event.venue}</div>
                      <div className="text-muted-foreground">{event.address}</div>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <UserCircle2 size={18} className="mt-1 text-muted-foreground" />
                    <div>
                      <div className="font-semibold text-foreground">{event.organizerName}</div>
                      <div className="text-muted-foreground">{event.organizerEmail}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="surface-card p-6">
                <div className="mb-2 text-xs font-semibold uppercase tracking-[0.24em] text-aan-bright">Need another event?</div>
                <h3 className="font-display text-2xl font-black uppercase text-foreground">Keep browsing AAN tickets</h3>
                <p className="mt-3 text-sm leading-7 text-muted-foreground">
                  Switch events without leaving the African Art Network ticketing experience.
                </p>
                <Link to="/screenings" className="mt-4 inline-flex rounded-full bg-aan-green px-4 py-3 font-display text-sm font-black uppercase text-black hover:bg-aan-bright">
                  View all events
                </Link>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
